import React, { useEffect, useMemo, useState } from 'react';
import { useAppState } from '../../BikesAppState';
import { FlowNode } from '../../Types/Types';
import { productsForSelectedAnswers } from '../../Utils/ProductFiltering.ts';
import AddToCartButton from '../../../NewFitAssistant/Components/Buttons/AddToCartButton.tsx';
import Buttons from '../Buttons/Buttons';
import PrimaryButton from '../Buttons/PrimaryButton';
import SecondaryButton from '../Buttons/SecondaryButton';

import './AdapterSelector.less';

type AdapterSelectorProps = {
  node: FlowNode;
};

const AdapterSelector = ({ node }: AdapterSelectorProps) => {
  const {
    fitAssistantBikesData,
    resetFlow,
    answers,
    products,
    setShowFitAssistant,
    adapterAtcDataByProductId,
    setAdapterAtcDataForProductId,
  } = useAppState();
  const cmsStep = fitAssistantBikesData?.Steps?.find((stepData) => stepData.Parameter === node.cmsParameter);
  const translations = fitAssistantBikesData?.Translations;
  const matchedProduct = productsForSelectedAnswers(products, answers)[0];
  const isExtranet = window._THULEDATA?.isExtranet ?? false;
  const cachedAtcData = matchedProduct ? adapterAtcDataByProductId[matchedProduct.ProductId] : undefined;
  const addToCartButtonData = cachedAtcData?.addToCartButton ?? null;
  const priceContainerData = cachedAtcData?.priceContainer ?? null;
  const hasLoadedAtcData = cachedAtcData?.isLoaded ?? false;
  const [adapterSelected, setAdapterSelected] = useState(false);
  const shouldShowMissingButtonFallback = useMemo(
    () => !isExtranet && hasLoadedAtcData && (!addToCartButtonData || !priceContainerData),
    [isExtranet, hasLoadedAtcData, addToCartButtonData, priceContainerData]
  );

  useEffect(() => {
    if (!matchedProduct || isExtranet) {
      return undefined;
    }

    if (adapterAtcDataByProductId[matchedProduct.ProductId]?.isLoaded) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/sitecore/fitguide/GetFitGuideATCAndPrice', {
          method: 'POST',
          headers: new Headers({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify({
            ProductIds: [matchedProduct.ProductId],
          }),
        });
        const data = await res.json();

        if (!cancelled) {
          setAdapterAtcDataForProductId(matchedProduct.ProductId, {
            addToCartButton: data?.Button ?? null,
            priceContainer: data?.PriceContainer ?? null,
            isLoaded: true,
          });
        }
      } catch {
        if (!cancelled) {
          setAdapterAtcDataForProductId(matchedProduct.ProductId, {
            addToCartButton: null,
            priceContainer: null,
            isLoaded: true,
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [matchedProduct, isExtranet, adapterAtcDataByProductId, setAdapterAtcDataForProductId]);

  return (
    <>
      <div className="adapter-selector">
        <div className="adapter-selector-content">
          {cmsStep?.ImageUrl && (
            <div className="cms-image-wrapper d-flex justify-content-center w-100 mb-9">
              <img src={cmsStep.ImageUrl} alt={cmsStep.ImageAlt} />
            </div>
          )}
          {cmsStep?.Text && <div dangerouslySetInnerHTML={{ __html: cmsStep.Text }}></div>}

          <div
            className={`adapter d-flex mt-10 mb-4 px-1 py-1${adapterSelected ? ' selected' : ''}`}
            onClick={() => setAdapterSelected((prev) => !prev)}
          >
            <div className="image-wrapper d-flex justify-content-center w-100">
              {matchedProduct?.Image && <img src={matchedProduct?.Image} alt={matchedProduct?.Webtitle1}></img>}
            </div>
            <div className="info d-flex flex-column justify-content-center">
              <h6 className={`${!matchedProduct?.Webtitle1 ? 'd-none' : 'mb-1'}`}>{matchedProduct?.Webtitle1}</h6>
              <p className={`${!matchedProduct?.Webtitle2 ? 'd-none' : 'paragraph--xs'}`}>
                {matchedProduct?.Webtitle2}
              </p>
              <h6 className={`${!matchedProduct.Price.PriceFormatted ? 'd-none' : ''}`}>
                + {matchedProduct.Price.PriceFormatted}
              </h6>
            </div>
            <div className="checkmark-container">{adapterSelected && <i className="le-icon-check"></i>}</div>
          </div>
        </div>
      </div>
      <Buttons visible={true}>
        {!isExtranet && addToCartButtonData && priceContainerData && (
          <AddToCartButton
            addToCartButtonData={addToCartButtonData}
            priceContainer={priceContainerData}
            onComplete={() => setShowFitAssistant(false)}
            disabled={!adapterSelected}
          />
        )}
        {shouldShowMissingButtonFallback && (
          <PrimaryButton visible={true} disabled text={translations?.addToCart ?? ''} onClick={() => {}} />
        )}
        {isExtranet && (
          <PrimaryButton
            visible={true}
            disabled={false}
            text={translations?.close ?? ''}
            onClick={() => setShowFitAssistant(false)}
          />
        )}
        <SecondaryButton visible={true} disabled={false} text={translations?.reset ?? ''} onClick={resetFlow} />
      </Buttons>
    </>
  );
};

export default AdapterSelector;
